/**
 * 万里汇（WorldFirst）合作落地页 —— 页面入口
 * 路由：/worldfirst   pageName：worldfirst
 *
 * 需求来源：docs/万里汇合作落地页/万里汇合作落地页-提示词.md 第1次提交
 *           （任务单 DEV-124229，父单 DEV-124227）；视觉基准同目录 万里汇合作页面.html。
 *
 * 页面结构（与原型 8 个顶层 section 一一对应）：
 *   Hero          — cjwf-dark-banner    粉→黑渐变 Hero + 多币种账户仪表盘
 *   Bonus         — cjwf-bonus          $100 奖励双卡 + reward 条
 *   Steps         — wf-steps-section    三步领取
 *   Business      — wf-business-section 全球收付款 3 卡
 *   MultiCurrency — wf-multi-section    多币种账户 6 卡
 *   EventsSlider  — cj-suggestion-slider 线下活动无限轮播
 *   Faq           — cjwf-premium-faq    9 条手风琴
 *   BottomCta     — cjwf-cta            底部 CTA
 *
 * 注意事项：
 * 1. 导出方式与 entity/cjBusinessTravel/alternatives 一致：`Component.fetch = fetchData` 后
 *    用 withSSR 包裹；不引入 Header/Footer —— 它们由框架 Layout 经 commonTopSsr 中间件统一注入。
 * 2. 【跨仓依赖 · 发版顺序】本页头部形态（透明融合、窄屏 100vw、左侧菜单零占位、彩色 logo）
 *    完全依赖 mf-components 按路由自判，egg 侧没有也无法覆盖那些清单。已在 mf 登记 5 处
 *    （flowVersionPaths / getPageName / getPageLogo / noMenuPages / block-search-nav.less 深底配色清单，
 *    见任务单 DEV-124228）。**mf 必须先于本仓发版**，否则深色 Hero 上会出现白色横条，
 *    且 iPad(834)/mobile(375) 会横向溢出 366px / 825px。
 * 3. 图片走公共资源仓（static-resource-platform/resources/egg/worldfirst），按 cdnHost 拼路径；
 *    本地 dev 的 CDN_HOST 指向**测试环境 OSS 桶**，图片须先发到测试环境才看得到（见 images.js 注释）。
 * 4. 文案统一走 i18next 取词（pageId=worldfirst），词库 public/lang/local/worldfirst.json。
 * 5. 本页无滚动触发动画 —— 原型的 38 个动画全部是加载即启动的 CSS 循环、1 个一次性 forwards
 *    动画与 hover 过渡，`IntersectionObserver`/`scroll` 监听均 0 处，故**不引入** useScrollReveal。
 */
import React, { useMemo } from 'react';
import withSSR from '../../hoc/withSSR';
import fetchData from './fetch';
import './index.less';
import { getConstants } from './constants';
import { url as imgUrl } from './images';

import Hero from './components/Hero';
import Bonus from './components/Bonus';
import Steps from './components/Steps';
import Business from './components/Business';
import MultiCurrency from './components/MultiCurrency';
import EventsSlider from './components/EventsSlider';
import Faq from './components/Faq';
import BottomCta from './components/BottomCta';

function WorldFirstPage(props) {
  const pageData = (props && props.pageData) || {};
  const cdnHost = pageData.cdnHost || (typeof CDN_HOST !== 'undefined' ? CDN_HOST : '');
  const constants = useMemo(() => getConstants(cdnHost), [cdnHost]);

  /**
   * 原型有 2 张图写在 CSS 的 background-image 里（Hero 底图、FAQ 底图），而 less 构建期拿不到 cdnHost。
   * 故 index.less 写成 url(var(--wf-hero-bg))/url(var(--wf-faq-bg))，这里按 cdnHost 算出实际地址注入。
   * 不注入的话那两处会解析失败、底图直接不显示（原先残留原型远程地址则会绕过公共资源仓）。
   */
  // 变量值是**完整的 `url("...")`**，index.less 里直接写 `var(--wf-hero-bg)`。
  // 为什么不在 less 里写 `url(var(--wf-hero-bg))`：webpack 的 css-loader 会把 url() 当资源路径解析，
  // 报 `Can't resolve './var(--wf-hero-bg'`（standalone lessc 不报，只有走 webpack 构建才炸）。
  const bgVars = useMemo(() => ({
    '--wf-hero-bg': 'url("' + imgUrl(cdnHost, 'hero_banner_bg.jpg') + '")',
    '--wf-faq-bg': 'url("' + imgUrl(cdnHost, 'faq_bg.jpg') + '")',
  }), [cdnHost]);

  return (
    <div className="worldfirst-page" data-gtag-module="worldfirst" style={bgVars}>
      <Hero data={constants.HERO} />
      <Bonus data={constants.BONUS} />
      <Steps data={constants.STEPS} />
      <Business data={constants.BUSINESS} />
      <MultiCurrency data={constants.MULTI} />
      <EventsSlider data={constants.EVENTS} />
      <Faq data={constants.FAQ} />
      <BottomCta data={constants.CTA} />
    </div>
  );
}

WorldFirstPage.fetch = fetchData;

export default withSSR(WorldFirstPage);
